import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Divider,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
  Label,
  Message,
} from "semantic-ui-react";

import moment, { Moment } from "moment";

import Android12Switch from "react-lib/appcon/common/Android12Switch";

import TimeComboBox from "react-lib/apps/common/TimeComboBox";

import {
  ApplyNewTimeParamsType,
  ApproveTimeParamsType,
  calculateAttendance,
  calculateNetWorkingHours,
  calculateTotalHours,
  calculateWorkingHours,
  ClockRecordParamsType,
  ClockRecordType,
  EventsTimeShiftType,
  formatTimeIfValid,
  getFieldsToClear,
  ShiftTimeWorkType,
  TimeSheetType,
} from "./sequence/SetupWorkSchedule";

// Types
type CardScheduleTimeShiftProps = {
  id?: string;
  clockRecords?: ClockRecordType[];
  dailyEvents: EventsTimeShiftType[];
  data: EventsTimeShiftType;
  dayInCalendar: Moment;
  index: number;
  isReadonly?: boolean;
  leaveTime?: NonNullable<EventsTimeShiftType["work_shift"]>[number];
  open: boolean;
  shiftLength: number;
  onApplyNewTime?: (data: ApplyNewTimeParamsType) => void;
  onApproveTime?: (data: ApproveTimeParamsType) => void;
  onGetClockRecord?: (data: ClockRecordParamsType) => void;
};

type DefaultClockRecordType = { clockInTime?: string; clockOutTime?: string } | null;

type Styles = Record<"switchStyle", CSSProperties>;

const styles: Styles = {
  switchStyle: { margin: "-4px -7px 0 -15px", transform: "scale(0.7)" },
};

const DEFAULT_SHIFT_TIME_WORK: ShiftTimeWorkType = {
  end_break_time: "",
  end_work_time: "",
  start_break_time: "",
  start_work_time: "",
};

const CardScheduleTimeShift = (props: CardScheduleTimeShiftProps) => {
  const [timeSheets, setTimeSheets] = useState<TimeSheetType>({});
  const [isScheduledOff, setIsScheduledOff] = useState(false);
  const [isEditingShiftTime, setIsEditingShiftTime] = useState(false);
  const [shiftTimeWork, setShiftTimeWork] = useState<ShiftTimeWorkType>({
    ...DEFAULT_SHIFT_TIME_WORK,
  });
  const [errMsg, setErrMsg] = useState<string>("");
  const [isApplyLoading, setIsApplyLoading] = useState(false);
  const [isApproveTime, setIsApproveTime] = useState(false);

  const isMounted = useRef(false);
  const defaultClockRecordRef = useRef<DefaultClockRecordType>(null);
  const shiftTimeWorkRef = useRef<ShiftTimeWorkType | null>(null);

  shiftTimeWorkRef.current = shiftTimeWork;

  // Memo Effect
  const clockInOptions = useMemo(() => {
    const clockRecords = (props.clockRecords ?? []).filter((item) => item.type === "I");

    return clockRecords.map((item) => ({
      datetime: item.clock_datetime,
      key: item.id,
      text: moment(item.clock_datetime).format("HH:mm"),
      value: item.id,
    }));
  }, [props.clockRecords]);

  const clockOutOptions = useMemo(() => {
    const clockRecords = (props.clockRecords ?? []).filter((item) => item.type === "O");

    return clockRecords.map((item) => {
      const date = moment(item.clock_datetime);
      const text = date.format("HH:mm");

      return {
        datetime: item.clock_datetime,
        key: item.id,
        text,
        value: item.id,
        content: (
          <div>
            <span>{text}</span>
            {date.isAfter(props.dayInCalendar, "day") && (
              <label
                style={{
                  color: "rgba(217, 2, 2, 1)",
                  fontWeight: "normal",
                  paddingLeft: "0.5rem",
                }}
              >
                Next day
              </label>
            )}
          </div>
        ),
      };
    });
  }, [props.clockRecords, props.dayInCalendar]);

  useEffect(() => {
    if (!isMounted.current && props.open) {
      props.onGetClockRecord?.(props.data);

      isMounted.current = true;
    }

    if (!props.open || props.data.shift_status === "O") {
      setIsScheduledOff(props.data.shift_status === "O");
      setTimeSheets({});

      return;
    }

    const clockIn = clockInOptions.find((option) => option.value === props.data.clock_in);
    const clockOut = clockOutOptions.find((option) => option.value === props.data.clock_out);

    // Auto-fill เฉพาะเมื่อไม่ใช่ OFF และมี option เดียว
    const shouldAutoFill = props.data.approval_status !== "A";

    let clockInValue =
      shouldAutoFill && clockInOptions.length === 1 ? clockInOptions[0].value : null;
    let clockOutValue =
      shouldAutoFill && clockOutOptions.length === 1 ? clockOutOptions[0].value : null;

    if (defaultClockRecordRef.current) {
      clockInValue = clockInOptions.find(
        (option) => option.datetime === defaultClockRecordRef.current?.clockInTime
      )?.value;
      clockOutValue = clockOutOptions.find(
        (option) => option.datetime === defaultClockRecordRef.current?.clockOutTime
      )?.value;
    }

    defaultClockRecordRef.current = null;

    setTimeSheets({
      clock_in: clockIn?.value ?? clockInValue,
      clock_out: clockOut?.value ?? clockOutValue,
      OT_clock_in_1_5hr: props.data.OT_clock_in_1_5hr,
      OT_clock_in_1hr: props.data.OT_clock_in_1hr,
      OT_clock_in_3hr: props.data.OT_clock_in_3hr,
      OT_clock_out_1_5hr: props.data.OT_clock_out_1_5hr,
      OT_clock_out_1hr: props.data.OT_clock_out_1hr,
      OT_clock_out_3hr: props.data.OT_clock_out_3hr,
    });
  }, [clockInOptions, clockOutOptions, props.open]);

  //  OT fields validation
  useEffect(() => {
    const clockInTime = clockInOptions.find((option) => option.value === timeSheets.clock_in);
    const clockOutTime = clockOutOptions.find((option) => option.value === timeSheets.clock_out);

    if (!clockInTime?.datetime || !clockOutTime?.datetime) {
      return;
    }

    const fieldsToClear = getFieldsToClear({
      endTime: clockOutTime.datetime,
      fields: [
        { end: "OT_clock_out_1hr", start: "OT_clock_in_1hr" },
        { end: "OT_clock_out_1_5hr", start: "OT_clock_in_1_5hr" },
        { end: "OT_clock_out_3hr", start: "OT_clock_in_3hr" },
      ],
      startTime: clockInTime.datetime,
      times: timeSheets,
    });

    // อัปเดต state เฉพาะเมื่อมี field ที่ต้อง Clear ค่า
    if (Object.keys(fieldsToClear).length > 0) {
      setTimeSheets((prev) => ({
        ...prev,
        ...fieldsToClear,
      }));
    }
  }, [clockInOptions, clockOutOptions, props.data.shift_type, timeSheets]);

  // Break time validation
  useEffect(() => {
    const clockInTime = shiftTimeWork.start_work_time;
    const clockOutTime = shiftTimeWork.end_work_time;

    if (!clockInTime || !clockOutTime) {
      return;
    }

    const date = props.dayInCalendar.format("YYYY-MM-DD");

    const fieldsToClear = getFieldsToClear({
      date,
      endTime: clockOutTime,
      fields: [{ end: "end_break_time", start: "start_break_time" }],
      startTime: clockInTime,
      times: shiftTimeWork,
    });

    const validStartBreak = formatTimeIfValid({
      date,
      endTime: shiftTimeWork.end_work_time,
      isOutsideRange: false,
      startTime: shiftTimeWork.start_work_time,
      timeValue: shiftTimeWork.start_break_time,
    });

    if (!validStartBreak && shiftTimeWork.start_break_time && shiftTimeWork.end_break_time) {
      fieldsToClear.start_break_time = "";
    }

    // อัปเดต state เฉพาะเมื่อมี field ที่ต้อง Clear ค่า
    if (Object.keys(fieldsToClear).length > 0) {
      setShiftTimeWork((prev) => ({
        ...prev,
        ...fieldsToClear,
      }));
    }
  }, [props.dayInCalendar, shiftTimeWork]);

  const isApproved = useMemo(() => ["A"].includes(props.data.approval_status || ""), [props.data]);

  const isDisabledToggleOff = useMemo(() => {
    const isStartAtMidnight = props.data.start.format("HH:mm") === "00:00";
    const isEndAtMidnight = props.data.end.format("HH:mm") === "23:59";
    const isFullDayShift = isStartAtMidnight && isEndAtMidnight;

    // เมื่อมีการ Approve แล้ว หรือ slot นั้นเป็น slot off
    return isApproved || isFullDayShift;
  }, [isApproved]);

  const breakTime = useMemo(() => {
    const { data } = props;

    return data.start_break_time && data.end_break_time
      ? `${data.start_break_time} - ${data.end_break_time}`
      : "-";
  }, [props.data]);

  const workingHours = useMemo(
    () => calculateWorkingHours([props.data], props.dayInCalendar),
    [props.data, props.dayInCalendar]
  );

  const leaveHours = useMemo(() => {
    if (!props.leaveTime) {
      return 0;
    }

    return calculateNetWorkingHours(props.leaveTime.start, props.leaveTime.end, props.leaveTime);
  }, [props.dayInCalendar, props.leaveTime]);

  const clockRecord = useMemo(() => {
    const clockInTime = clockInOptions.find((option) => option.value === timeSheets.clock_in);
    const clockOutTime = clockOutOptions.find((option) => option.value === timeSheets.clock_out);

    return { clockInTime: clockInTime?.datetime, clockOutTime: clockOutTime?.datetime };
  }, [clockInOptions, clockOutOptions, timeSheets.clock_in, timeSheets.clock_out]);

  const attendance = useMemo(() => {
    let clockIn = clockRecord.clockInTime;

    // หากมีการลาให้ลบชั่วโมง Clock in เพื่อให้ Actual work hours มีชั่วโมงทำงานถูกต้อง
    if (clockIn && props.leaveTime) {
      const deductionHours = calculateTotalHours(props.leaveTime.start, props.leaveTime.end);

      clockIn = moment(clockIn).add(-deductionHours, "hours").toISOString();
    }

    return calculateAttendance({
      clockIn,
      clockOut: clockRecord.clockOutTime,
      scheduledHours: workingHours.totalHours,
      shift: props.data,
    });
  }, [leaveHours, props.data, timeSheets, workingHours.totalHours]);

  const overtimeHours = useMemo(() => {
    const result = {
      ot1: "",
      ot1_5: "",
      ot3: "",
    };

    const otTypes = [
      {
        inTime: timeSheets.OT_clock_in_1hr,
        key: "ot1" as const,
        outTime: timeSheets.OT_clock_out_1hr,
      },
      {
        inTime: timeSheets.OT_clock_in_1_5hr,
        key: "ot1_5" as const,
        outTime: timeSheets.OT_clock_out_1_5hr,
      },
      {
        inTime: timeSheets.OT_clock_in_3hr,
        key: "ot3" as const,
        outTime: timeSheets.OT_clock_out_3hr,
      },
    ];

    for (const { inTime, key, outTime } of otTypes) {
      if (inTime && outTime) {
        const otHours = calculateNetWorkingHours(inTime, outTime, props.data);

        result[key] = otHours.toString();
      }
    }

    return result;
  }, [props.data, props.dayInCalendar, timeSheets]);

  const actualWorkHour = useMemo(() => {
    if (isScheduledOff) {
      return "OFF";
    }

    if (props.data.approval_status === "A" && props.data.shift_status === "X") {
      return "0 hr";
    }

    return attendance.actualWorkHours > 0 ? `${attendance.actualWorkHours - leaveHours} hr` : "-";
  }, [
    attendance.actualWorkHour,
    isScheduledOff,
    leaveHours,
    props.data.approval_status,
    props.data.shift_status,
  ]);

  const disabledApprove = useMemo(() => {
    const needsApproval = attendance.isOutsideShift && props.data.approval_status !== "A";

    return needsApproval || !!props.isReadonly;
  }, [attendance, props.data, props.isReadonly]);

  const handleChangeValue = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    setTimeSheets((prev) => ({ ...prev, [name]: data.value }));
  };

  const handleChangeScheduleOff = (e: SyntheticEvent, checked: boolean) => {
    if (checked) {
      setTimeSheets({});
      handleCloseEditShiftTime();

      if (props.shiftLength > 1) {
        setErrMsg(`The system has detected more than two shift times. Request for OFF is not allowed.
              Please remove one of the shifts before processing.`);

        return;
      }
    }

    setIsScheduledOff(checked);
  };

  const handleChangeTime = (name: string) => (value: string) => {
    handleChangeValue(null, { name, value });
  };

  const handleToggleEditingShiftTime = () => {
    if (isEditingShiftTime) {
      handleCloseEditShiftTime();
    } else {
      setIsEditingShiftTime(true);
    }
  };

  const handleCloseEditShiftTime = () => {
    setIsEditingShiftTime(false);
    setIsApplyLoading(false);

    setShiftTimeWork({ ...DEFAULT_SHIFT_TIME_WORK });

    handleCloseErrMsg();
  };

  const handleChangeShiftTime = (key: string) => (value: string) => {
    setShiftTimeWork((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyNewTime = () => {
    const shiftTime = shiftTimeWorkRef.current;

    // กรณีไม่ได้ระบุช่อง break time
    if (!!shiftTime?.start_break_time !== !!shiftTime?.end_break_time) {
      setErrMsg("Please enter the break time.");

      return;
    }

    const filteredEvents = props.dailyEvents.filter(
      (event) => !event.start.isSame(props.data.start) && !event.end.isSame(props.data.end)
    );

    const hasConflictingEvent = filteredEvents.some((event) => {
      const date = props.dayInCalendar.format("YYYY-MM-DD");

      return (
        event.start.isSame(moment(`${date} ${shiftTime?.start_work_time}`)) &&
        event.end.isSame(moment(`${date} ${shiftTime?.end_work_time}`))
      );
    });

    // กรณีมีการแก้ไขเวลา และแก้ไขเป็นเวลาที่ซ้ำกับ slot ก่อนหน้าในวัน
    if (hasConflictingEvent) {
      setErrMsg(
        "Cannot save the data because a shift time already exists during the selected period."
      );

      return;
    }

    setIsApplyLoading(true);

    handleCloseErrMsg();

    props.onApplyNewTime?.({
      data: shiftTime ?? {},
      event: props.data,
      index: props.index,
      onError: handleCloseEditShiftTime,
      onSuccess: handleCloseEditShiftTime,
    });
  };

  const handleCloseErrMsg = () => {
    setErrMsg("");
  };

  const handleApprove = () => {
    setIsApproveTime(true);

    const isFullDayLeave =
      leaveHours && workingHours.totalHours ? leaveHours >= workingHours.totalHours : false;

    // เมื่อ Refresh clock record ให้ default clock record เดิม
    defaultClockRecordRef.current = clockRecord;

    props.onApproveTime?.({
      data: timeSheets,
      event: props.data,
      index: props.index,
      isFullDayLeave,
      isScheduledOff,
      onError: () => {
        defaultClockRecordRef.current = null;

        setIsApproveTime(false);
      },
      onSuccess: async () => {
        setIsApproveTime(false);
      },
    });
  };

  const handleFormatBeforeChange = (value: string) =>
    formatTimeIfValid({
      endTime: clockRecord.clockOutTime,
      isOutsideRange: attendance.isOutsideShift,
      startTime: clockRecord.clockInTime,
      timeValue: value,
    });

  return (
    <div style={{ color: "rgba(57, 55, 55, 1)", minWidth: "29rem" }}>
      {!!errMsg && (
        <Message style={{ fontSize: "0.825rem", marginBottom: 0 }} error>
          <div
            style={{ alignItems: "center", display: "grid", gridTemplateColumns: "auto 1fr auto" }}
          >
            <Icon style={{ marginTop: "-0.5rem" }} name="warning sign" />
            <label style={{ paddingLeft: "0.25rem" }}>{errMsg}</label>
            <Icon
              style={{
                position: "absolute",
                right: "3px",
                top: "5px",
              }}
              name="close"
              link
              onClick={handleCloseErrMsg}
            />
          </div>
        </Message>
      )}

      <div style={{ padding: "0.5rem 0.5rem" }}>
        <div
          id={`CardScheduleTimeShift-Div-${props.id}`}
          style={{
            display: "flex",
            fontWeight: "bold",
            justifyContent: "space-between",
            marginBottom: "0.25rem",
          }}
        >
          <div>Scheduled shift time</div>
          <div>{isApproved && <span style={{ color: "rgba(27, 157, 44, 1)" }}>APPROVE</span>}</div>
        </div>
        <div>
          <label>
            Work time :{" "}
            {props.data.shift_status === "O"
              ? "OFF"
              : `${props.data.start.format("HH:mm")} - ${props.data.end.format("HH:mm")}`}
          </label>
          <label style={{ paddingLeft: "1.5rem" }}>Break time : {breakTime}</label>
        </div>
        <div>
          Total work hour : {props.data.shift_status === "O" ? "OFF" : workingHours.totalHours}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "-0.5rem",
            marginTop: "0.5rem",
          }}
        >
          <Button
            id={`CardScheduleTimeShift-Button-EditShiftTime-${props.id}`}
            color="blue"
            disabled={isApproved || isScheduledOff}
            size="tiny"
            onClick={handleToggleEditingShiftTime}
          >
            Edit Scheduled shift time
          </Button>
        </div>
        {isEditingShiftTime && (
          <Form style={{ margin: "1rem 0px -0.5rem" }} size="tiny">
            <FormGroup style={{ margin: 0 }}>
              <FormField width={4}>
                <label style={{ fontWeight: "normal" }}>Work time</label>
                <TimeComboBox
                  id={`CardScheduleTimeShift-TimeComboBox-StartTime-${props.id}`}
                  defaultValue={shiftTimeWork.start_work_time}
                  onTextChange={handleChangeShiftTime("start_work_time")}
                />
              </FormField>
              <FormField>
                <label>{"\u00A0"}</label>
                <label style={{ margin: "0.75rem -0.25rem 0" }}>To</label>
              </FormField>
              <FormField width={4}>
                <label>{"\u00A0"}</label>
                <TimeComboBox
                  id={`CardScheduleTimeShift-TimeComboBox-EndTime-${props.id}`}
                  defaultValue={shiftTimeWork.end_work_time}
                  onTextChange={handleChangeShiftTime("end_work_time")}
                />
              </FormField>
              <FormField width={4}>
                <label style={{ fontWeight: "normal" }}>Break time</label>
                <TimeComboBox
                  id={`CardScheduleTimeShift-TimeComboBox-StartBreakTime-${props.id}`}
                  defaultValue={shiftTimeWork.start_break_time}
                  onTextChange={handleChangeShiftTime("start_break_time")}
                />
              </FormField>
              <FormField>
                <label>{"\u00A0"}</label>
                <label style={{ margin: "0.75rem -0.25rem 0" }}>To</label>
              </FormField>
              <FormField width={4}>
                <label>{"\u00A0"}</label>
                <TimeComboBox
                  id={`CardScheduleTimeShift-TimeComboBox-EndBreakTime-${props.id}`}
                  defaultValue={shiftTimeWork.end_break_time}
                  onTextChange={handleChangeShiftTime("end_break_time")}
                />
              </FormField>
            </FormGroup>
            <FormGroup
              style={{ justifyContent: "center", marginBottom: 0, transform: "scale(0.8)" }}
            >
              <FormField>
                <Button
                  id={`CardScheduleTimeShift-Button-ApplyNewTime-${props.id}`}
                  color="green"
                  disabled={!shiftTimeWork.start_work_time || !shiftTimeWork.end_work_time}
                  loading={isApplyLoading}
                  size="tiny"
                  onClick={() => {
                    // เมื่อเลือก Break time โดย onBlur ยังไม่เสร็จแล้วมากดปุ่ม Apply time จะยังได้ค่าเก่า
                    setTimeout(() => {
                      handleApplyNewTime();
                    });
                  }}
                >
                  APPLY NEW TIME
                </Button>
              </FormField>
              <FormField>
                <Button
                  id={`CardScheduleTimeShift-Button-CancelEditing-${props.id}`}
                  color="red"
                  size="tiny"
                  basic
                  onClick={handleCloseEditShiftTime}
                >
                  CANCEL EDITING
                </Button>
              </FormField>
            </FormGroup>
          </Form>
        )}
      </div>
      <Divider
        style={{ borderTop: "1px solid rgba(33, 133, 208, 1)", margin: "0.75rem 0 0.35rem" }}
      />
      <Form className="--override-disabled --grey" size="tiny">
        <FormGroup style={{ alignItems: "baseline", marginBottom: "0.25rem" }} inline>
          <FormField inline>
            <label>Actual working hours</label>
          </FormField>
          <FormField>
            <Android12Switch
              id={`CardScheduleTimeShift-Android12Switch-IsScheduleOff-${props.id}`}
              checked={isScheduledOff}
              disabled={isDisabledToggleOff}
              label="OFF"
              switchStyle={styles.switchStyle}
              onChange={handleChangeScheduleOff}
            />
          </FormField>
        </FormGroup>
        {props.leaveTime && (
          <FormGroup inline>
            <FormField style={{ position: "relative" }} width={9} inline>
              <label style={{ fontWeight: "normal", minWidth: "max-content" }}>Clock-in</label>
              <Input
                id={`CardScheduleTimeShift-Input-StartLeaveTime-${props.id}`}
                value={props.leaveTime.start.format("HH:mm")}
                fluid
                readOnly
              />
            </FormField>
            <FormField style={{ position: "relative" }} width={9} inline>
              <label style={{ fontWeight: "normal", minWidth: "max-content" }}>Clock-out</label>
              <Input
                id={`CardScheduleTimeShift-Input-EndLeaveTime-${props.id}`}
                value={props.leaveTime.end.format("HH:mm")}
                fluid
                readOnly
              />
            </FormField>
            <FormField>
              <Label style={{ minWidth: "max-content" }} color="red" basic>
                {props.leaveTime.title}
              </Label>
            </FormField>
          </FormGroup>
        )}
        <FormGroup inline>
          <FormField style={{ position: "relative" }} width={9} inline>
            <label style={{ fontWeight: "normal", minWidth: "max-content" }}>Clock-in</label>
            <Dropdown
              id={`CardScheduleTimeShift-Dropdown-ClockIn-${props.id}`}
              style={{ width: "100%" }}
              disabled={isScheduledOff || isApproved}
              name="clock_in"
              value={timeSheets.clock_in ?? ""}
              clearable
              fluid
              search
              selection
              options={clockInOptions}
              onChange={handleChangeValue}
            />
            {attendance.isLate && (
              <div
                id={`CardScheduleTimeShift-Div-IsLate-${props.id}`}
                style={{
                  bottom: "-16px",
                  color: "rgba(217, 2, 2, 1)",
                  fontWeight: "bold",
                  left: "22px",
                  position: "absolute",
                }}
              >
                LATE
              </div>
            )}
          </FormField>
          <FormField style={{ position: "relative" }} width={9} inline>
            <label style={{ fontWeight: "normal", minWidth: "max-content" }}>Clock-out</label>
            <Dropdown
              id={`CardScheduleTimeShift-Dropdown-ClockOut-${props.id}`}
              style={{ width: "100%" }}
              disabled={isScheduledOff || isApproved}
              name="clock_out"
              value={timeSheets.clock_out ?? ""}
              clearable
              fluid
              search
              selection
              options={clockOutOptions}
              onChange={handleChangeValue}
            />
            {attendance.isEarlyOut && (
              <div
                id={`CardScheduleTimeShift-Div-IsEarlyOut-${props.id}`}
                style={{
                  bottom: "-16px",
                  color: "rgba(217, 2, 2, 1)",
                  fontWeight: "bold",
                  left: "10px",
                  position: "absolute",
                }}
              >
                Early out
              </div>
            )}
          </FormField>
          <FormField>
            {props.data.approval_status === "A" && props.data.shift_status === "X" && (
              <div
                id={`CardScheduleTimeShift-Div-IsAbsent-${props.id}`}
                style={{ color: "rgba(217, 2, 2, 1)", fontWeight: "bold" }}
              >
                ABSENT
              </div>
            )}
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField id={`CardScheduleTimeShift-FormField-ActualWorkHour-${props.id}`} inline>
            Actual work hour :{" "}
            <span style={{ color: attendance.isLate ? "rgba(198, 0, 0, 1)" : "" }}>
              {actualWorkHour}
            </span>
          </FormField>
          <FormField id={`CardScheduleTimeShift-FormField-LeaveHour-${props.id}`} inline>
            Leave hour : {props.leaveTime ? leaveHours : "-"} hr
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField id={`CardScheduleTimeShift-FormField-LateDetail-${props.id}`} inline>
            Late : {attendance.lateMin}
          </FormField>
          <FormField id={`CardScheduleTimeShift-FormField-EarlyOutDetail-${props.id}`} inline>
            Early out : {attendance.earlyOutMin}
          </FormField>
        </FormGroup>
        <FormGroup style={{ marginBottom: "0.5rem" }} inline>
          <FormField style={{ minWidth: "75px" }} inline>
            <label>OT 1</label>
            <label>(hr.)</label>
          </FormField>
          <FormField width={3} inline>
            <Input
              id={`CardScheduleTimeShift-Input-OT1Hr-${props.id}`}
              style={{ marginTop: "-6px" }}
              value={overtimeHours.ot1}
              disabled
              fluid
            />
          </FormField>
          <FormField width={6} inline>
            <div style={{ width: "100%" }}>
              <TimeComboBox
                id={`CardScheduleTimeShift-TimeComboBox-StartClockIn1Hr-${props.id}`}
                defaultValue={timeSheets.OT_clock_in_1hr || ""}
                disabled={isApproved}
                onFormatBeforeChange={handleFormatBeforeChange}
                onTextChange={handleChangeTime("OT_clock_in_1hr")}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>to</label>
          </FormField>
          <FormField width={6} inline>
            <div style={{ width: "100%" }}>
              <TimeComboBox
                id={`CardScheduleTimeShift-TimeComboBox-EndClockIn1Hr-${props.id}`}
                defaultValue={timeSheets.OT_clock_out_1hr || ""}
                disabled={isApproved}
                onFormatBeforeChange={handleFormatBeforeChange}
                onTextChange={handleChangeTime("OT_clock_out_1hr")}
              />
            </div>
          </FormField>
        </FormGroup>

        <FormGroup style={{ marginBottom: "0.5rem" }} inline>
          <FormField style={{ minWidth: "75px" }} inline>
            <label>OT 1.5</label>
            <label>(hr.)</label>
          </FormField>
          <FormField width={3} inline>
            <Input
              id={`CardScheduleTimeShift-Input-OT1_5Hr-${props.id}`}
              style={{ marginTop: "-6px" }}
              value={overtimeHours.ot1_5}
              disabled
              fluid
            />
          </FormField>
          <FormField width={6} inline>
            <div style={{ width: "100%" }}>
              <TimeComboBox
                id={`CardScheduleTimeShift-TimeComboBox-StartClockIn1_5Hr-${props.id}`}
                defaultValue={timeSheets.OT_clock_in_1_5hr || ""}
                disabled={isApproved}
                onFormatBeforeChange={handleFormatBeforeChange}
                onTextChange={handleChangeTime("OT_clock_in_1_5hr")}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>to</label>
          </FormField>
          <FormField width={6} inline>
            <div style={{ width: "100%" }}>
              <TimeComboBox
                id={`CardScheduleTimeShift-TimeComboBox-EndClockIn1_5Hr-${props.id}`}
                defaultValue={timeSheets.OT_clock_out_1_5hr || ""}
                disabled={isApproved}
                onFormatBeforeChange={handleFormatBeforeChange}
                onTextChange={handleChangeTime("OT_clock_out_1_5hr")}
              />
            </div>
          </FormField>
        </FormGroup>

        <FormGroup style={{ marginBottom: "0.5rem" }} inline>
          <FormField style={{ minWidth: "75px" }} inline>
            <label>OT 3</label>
            <label>(hr.)</label>
          </FormField>
          <FormField width={3} inline>
            <Input
              id={`CardScheduleTimeShift-Input-OT3Hr-${props.id}`}
              style={{ marginTop: "-6px" }}
              value={overtimeHours.ot3}
              disabled
              fluid
            />
          </FormField>
          <FormField width={6} inline>
            <div style={{ width: "100%" }}>
              <TimeComboBox
                id={`CardScheduleTimeShift-TimeComboBox-StartClockIn3Hr-${props.id}`}
                defaultValue={timeSheets.OT_clock_in_3hr || ""}
                disabled={isApproved}
                onFormatBeforeChange={handleFormatBeforeChange}
                onTextChange={handleChangeTime("OT_clock_in_3hr")}
              />
            </div>
          </FormField>
          <FormField inline>
            <label>to</label>
          </FormField>
          <FormField width={6} inline>
            <div style={{ width: "100%" }}>
              <TimeComboBox
                id={`CardScheduleTimeShift-TimeComboBox-EndClockIn3Hr-${props.id}`}
                defaultValue={timeSheets.OT_clock_out_3hr || ""}
                disabled={isApproved}
                onFormatBeforeChange={handleFormatBeforeChange}
                onTextChange={handleChangeTime("OT_clock_out_3hr")}
              />
            </div>
          </FormField>
        </FormGroup>
        <FormGroup style={{ margin: "1rem 0 0.5rem" }} inline>
          <FormField style={{ flex: 1 }} />
          <FormField inline>
            <Button
              id={`btn-confirmData-${props.id}`}
              color={isApproved ? "red" : "green"}
              disabled={disabledApprove}
              loading={isApproveTime}
              size="tiny"
              onClick={handleApprove}
            >
              <Icon name="check circle" />
              <label>{isApproved ? "Unapprove" : "Approve"}</label>
            </Button>
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

CardScheduleTimeShift.displayName = "CardScheduleTimeShift";

export default React.memo(CardScheduleTimeShift);
